import { APP_SCROLL_ELEMENT } from '@doltech/core/lib/appScroll/withAppScroll';
import { components } from '@doltech/dto';
import { components as pageMntDefinitions } from '@doltech/pageManagement/dto';
import {
  MarkedTypeEnum,
  ModalNameEnum,
  PageTypeEnum,
  SkillEnum,
  TestModeEnum,
} from '@doltech/utils/lib/constants';
import { cast, types as t } from 'mobx-state-tree';

import { UserTest } from '../../question-data-model/user-test.store';
import { ResultListeningTestSection } from './TestSection/result-listening-test-section.store';
import { ResultReadingTestSection } from './TestSection/result-reading-test-section.store';

const PageSEO = t.model('PageSEO', {
  id: t.maybe<any>(t.string),
  templateTypeId: t.maybe<any>(t.string),
  url: t.maybe<any>(t.string),
});

export const TestResultStore = UserTest.named('TestResultStore')
  .props({
    initialed: t.optional(t.boolean, false),
    currentQuestionIndex: t.optional(t.number, 0),
    currentSectionIndex: t.optional(t.number, 0),
    currentGroupIndex: t.optional(t.number, 0),
    drawerEnabled: t.optional(t.boolean, false),
    currentDrawerName: t.optional<any>(t.string, ModalNameEnum.DEFAULT),
    bookCover: t.maybeNull<any>(t.string), // hard code
    testMode: t.optional<any>(t.string, TestModeEnum.TEST_RESULT),
    testSections: t.optional(
      t.maybeNull(
        t.array(
          t.union(
            {
              dispatcher: (s) => {
                switch (s.skill) {
                  case SkillEnum.READING:
                    return ResultReadingTestSection;
                  case SkillEnum.LISTENING:
                    return ResultListeningTestSection;
                  default:
                    throw new Error(`Cannot resolve Test Section skill "${s.skill}"`);
                }
              },
            },
            ResultReadingTestSection
          )
        )
      ),
      []
    ),

    seoPage: t.optional(t.array(PageSEO), []),
  })
  .volatile(() => ({
    premiumStore: null,
    isLogged: false,
  }))
  .actions((self) => ({
    registerPremiumStore(newPremiumStore) {
      self.premiumStore = newPremiumStore;
    },
    setIsLogged(newLogged) {
      self.isLogged = newLogged;
    },
  }))
  .views((self) => ({
    get questions() {
      const questionArr = [];
      self.testSections.forEach((testSection) => {
        questionArr.push(...testSection.getQuestions());
      });
      return questionArr;
    },
    getCurrentTestSection() {
      return self.testSections.find(
        (_testSection, testSectionIndex) => testSectionIndex === self.currentSectionIndex
      );
    },
    getQuestionsGroupedBySection() {
      return self.testSections.map((section) => {
        const questions = section.getQuestions();
        return {
          section,
          questions,
        };
      });
    },
  }))
  .views((self) => ({
    get desc() {
      if (self.testSections) {
        return (
          self.testSections.find(
            (_testSection, testSectionIndex) => testSectionIndex === self.currentSectionIndex
          )?.name || ''
        );
      }
      return '';
    },
    get canNext() {
      return self.currentQuestionIndex < self.questions.length - 1;
    },
    get canPrev() {
      return self.currentQuestionIndex > 0;
    },
    get prevInfo() {
      return '';
    },
    get nextInfo() {
      return '';
    },
    getCurrentQuestion() {
      return self.questions.find(
        (_question, cQuestionIndex) => cQuestionIndex === self.currentQuestionIndex
      );
    },
    get currentQuestion() {
      return self.questions.find(
        (_question, questionIndex) => questionIndex === self.currentQuestionIndex
      );
    },
    getTotalWrongsBySection() {
      return self.testSections.map((section) => {
        let totalWrong = 0;
        section.getQuestions().forEach((question) => {
          if (MarkedTypeEnum.CORRECT !== question.markedStatus) {
            totalWrong += 1;
          }
        });
        return totalWrong;
      });
    },
    getCurrentGroup() {
      return self
        .getCurrentTestSection()
        .questionGroups.find((group, groupIndex: number) => self.currentGroupIndex === groupIndex);
    },
    getTotalQuestion() {
      return self.questions.length;
    },

    get seoVocabUrl() {
      return (
        (self?.seoPage || []).find((page) => page.templateTypeId === PageTypeEnum.VIEW_VOCAB)
          ?.url || ''
      );
    },
  }))
  .views((self) => ({
    getCurrentQuestionObject() {
      const { uuid } = self.currentQuestion;
      let currentQuestion = null;

      self.testSections.find((testSection) => {
        return testSection.questionGroups.find((group) => {
          return group.questions.find((question) => {
            if (question.uuid === uuid) {
              currentQuestion = question;
              return true;
            }
            return false;
          });
        });
      });
      return currentQuestion;
    },
  }))
  .actions((self) => ({
    preventUserAction() {
      // return (!self.isLogged || self?.premiumStore?.isExpired) && self.skill === SkillEnum.READING;
      return !self.isLogged;
    },
    setDrawerEnabled(enable, drawerName?: ModalNameEnum) {
      self.drawerEnabled = enable;
      self.currentDrawerName = drawerName;
    },
    setTestMode(testMode: TestModeEnum) {
      self.testMode = testMode;
    },
  }))
  .actions((self) => ({
    goToQuestion(questionNo, isScroll = true) {
      const currentQuestionIndex = questionNo - 1;
      const questionUUID = self.questions[currentQuestionIndex]?.uuid;

      if (questionUUID) {
        self.currentQuestionIndex = currentQuestionIndex;
        let groupIndex = 0;

        self.currentSectionIndex =
          self.testSections.findIndex((testSection) => {
            return testSection.questionGroups.find((group, index) => {
              groupIndex = index;
              return group.questions.find((question) => {
                return question.uuid === questionUUID;
              });
            });
          }) || 0;
        self.currentGroupIndex = groupIndex;
        self.setDrawerEnabled(false);
        if (isScroll) {
          const elm: HTMLElement = document.getElementById('body-container');
          const appWrapperElm: HTMLElement = APP_SCROLL_ELEMENT;
          if (elm) {
            elm.scrollTop = 0;
          }
          if (appWrapperElm) {
            appWrapperElm.scrollTop = 0;
          }
        }
      }
    },
  }))
  .actions((self) => ({
    init(
      data: components['schemas']['UserFullTestDTO'] | components['schemas']['UserPracticeTestDTO']
    ) {
      self.initialed = true;

      self.createdAt = data.createdAt;
      self.id = data.id;
      self.lastModifiedAt = data.lastModifiedAt;
      self.testType = data.testType as any;
      switch (data.testType) {
        case 'FULL_TEST': {
          const testData: components['schemas']['UserFullTestDTO'] = data;
          self.bookCover = testData.test?.coverImage?.url;
          self.name = testData.test.name;
          self.skill = testData.test.skill as SkillEnum;
          self.totalDuration = testData.test.totalDuration * 1000;
          self.totalSections = testData.testSections.length;
          self.testSections.replace(testData.testSections);
          break;
        }
        case 'PRACTICE_TEST': {
          const testData: components['schemas']['UserPracticeTestDTO'] = data;
          self.bookCover = testData.testSection?.coverImage?.url;
          self.name = testData.testSection.name;
          self.skill = testData.testSection.skill as SkillEnum;
          self.totalDuration = testData.testSection.duration * 1000;
          self.totalSections = 1;
          self.testSections.replace([testData.testSection]);
          break;
        }
        default:
      }

      const pageData = (data as pageMntDefinitions['schemas']['TestDetailDTO'])?.pages || [];

      self.seoPage.replace(pageData as any);
      self.testResults = cast(data?.testResult);
    },

    next() {
      if (self.preventUserAction()) return;
      if (self.canNext) {
        const nextQuestionIndex = self.currentQuestionIndex + 1;
        const nextQuestionNo = nextQuestionIndex + 1;
        self.goToQuestion(nextQuestionNo, self.questions[nextQuestionIndex].scroll);
      }
    },
    prev() {
      if (self.preventUserAction()) return;

      if (self.canPrev) {
        const prevQuestionIndex = self.currentQuestionIndex - 1;
        const prevQuestionNo = prevQuestionIndex + 1;
        self.goToQuestion(prevQuestionNo, self.questions[prevQuestionIndex].scroll);
      }
    },

    close() {},
  }));
